import { MantineColor } from "@mantine/core";

/**
 * Travel status values for tracking trip lifecycle.
 * Numeric values allow for ordering and comparison.
 */
export const TRAVEL_STATUSES = {
  IN_PROGRESS: 100, // Trip being recorded
  COMPLETED: 200, // Trip finished
  SUBMITTED: 300, // Submitted for reimbursement
  APPROVED: 400, // Reimbursement approved
  REJECTED: 500, // Reimbursement rejected
  REIMBURSED: 600, // Payment received
} as const;

export type TravelStatusValue =
  (typeof TRAVEL_STATUSES)[keyof typeof TRAVEL_STATUSES];

/**
 * Configuration for each travel status including icon, color, and translation key.
 */
export interface TravelStatusConfig {
  value: TravelStatusValue;
  /** Icon name for AppIcon component (without "Icon" prefix) */
  icon: string;
  color: MantineColor;
  translationKey: string;
}

/**
 * Travel status configurations mapped by value.
 */
export const TRAVEL_STATUS_CONFIG: Record<TravelStatusValue, TravelStatusConfig> = {
  [TRAVEL_STATUSES.IN_PROGRESS]: {
    value: TRAVEL_STATUSES.IN_PROGRESS,
    icon: "PlayerPlay",
    color: "blue",
    translationKey: "travels:statuses.inProgress",
  },
  [TRAVEL_STATUSES.COMPLETED]: {
    value: TRAVEL_STATUSES.COMPLETED,
    icon: "CircleCheck",
    color: "green",
    translationKey: "travels:statuses.completed",
  },
  [TRAVEL_STATUSES.SUBMITTED]: {
    value: TRAVEL_STATUSES.SUBMITTED,
    icon: "Send",
    color: "yellow",
    translationKey: "travels:statuses.submitted",
  },
  [TRAVEL_STATUSES.APPROVED]: {
    value: TRAVEL_STATUSES.APPROVED,
    icon: "ThumbUp",
    color: "teal",
    translationKey: "travels:statuses.approved",
  },
  [TRAVEL_STATUSES.REJECTED]: {
    value: TRAVEL_STATUSES.REJECTED,
    icon: "ThumbDown",
    color: "red",
    translationKey: "travels:statuses.rejected",
  },
  [TRAVEL_STATUSES.REIMBURSED]: {
    value: TRAVEL_STATUSES.REIMBURSED,
    icon: "Cash",
    color: "gray",
    translationKey: "travels:statuses.reimbursed",
  },
};

/**
 * Get travel status configuration by value.
 * Returns undefined if value is not a valid travel status.
 */
export const getTravelStatusConfig = (
  value: number | undefined | null
): TravelStatusConfig | undefined => {
  if (value === undefined || value === null) return undefined;
  return TRAVEL_STATUS_CONFIG[value as TravelStatusValue];
};

/**
 * Array of all travel status configurations for iteration.
 */
export const TRAVEL_STATUS_OPTIONS: TravelStatusConfig[] = Object.values(TRAVEL_STATUS_CONFIG);

/**
 * Default travel status for new travels.
 */
export const DEFAULT_TRAVEL_STATUS: TravelStatusValue = TRAVEL_STATUSES.IN_PROGRESS;

/**
 * Check if a travel is currently active (in progress).
 */
export const isTravelActive = (status: number | undefined | null): boolean => {
  return status === TRAVEL_STATUSES.IN_PROGRESS;
};

/**
 * Check if a travel is in a final state (completed workflow).
 */
export const isTravelFinal = (status: number | undefined | null): boolean => {
  return status === TRAVEL_STATUSES.REIMBURSED || status === TRAVEL_STATUSES.REJECTED;
};